import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService, {
  FeedbackResponse,
  DangerousGoodsResponse,
  UNNumbersResponse,
  HSCodesResponse,
  PreprocessingResponse,
  BookingClassificationResponse,
  DgmlTarget,
} from '../services/api';
import { FALLBACK_VERSIONS, groupEnabledVersions, latestVersion, ModelVersions } from '../services/modelVersions';
import AppShell, { NavItem } from './AppShell';
import { ScatterIcon, ShieldAlertIcon } from './icons';
import HsCodeResult from './HsCodeResult';
import PreprocessingResult from './PreprocessingResult';
import BookingResult from './BookingResult';
import ValidateEntries from './ValidateEntries';
import FeedbackModal, { FlaggedResult } from './FeedbackModal';
import EmbeddingsExplorer from './EmbeddingsExplorer';
import './DangerousGoodsClassifier.css';

type View = 'classify' | 'embeddings';

// What the classify form takes: one free-text description, or a whole booking as
// JSON, scored the way the live prediction job scores it
type InputMode = 'text' | 'booking';

// 3 is what live_dg_prediction stores per cargo, so the default view matches the live
// cases; the larger options are for seeing what else the model considered.
const DEFAULT_UN_COUNT = 3;
const UN_COUNT_OPTIONS = [1, 3, 5, 10];

// "3208.90, 381400" is two codes; a dot or space inside one is not a separator,
// because "3208 90" is how a code is often written. The backend does the rest.
function splitHsCodes(text: string): string[] {
  return text.split(/[,;]/).map((code) => code.trim()).filter(Boolean);
}

const DangerousGoodsClassifier: React.FC = () => {
  const { profile, logout } = useAuth();
  const [view, setView] = useState<View>('classify');
  const [targets, setTargets] = useState<DgmlTarget[]>([]);
  const [target, setTarget] = useState(apiService.getTarget());
  const [versions, setVersions] = useState<ModelVersions>(FALLBACK_VERSIONS);
  const [versionsFromServer, setVersionsFromServer] = useState<boolean | null>(null);
  const [inputMode, setInputMode] = useState<InputMode>('text');
  const [cargoText, setCargoText] = useState('');
  const [declaredHs, setDeclaredHs] = useState('');
  const [bookingText, setBookingText] = useState('');
  const [customer, setCustomer] = useState('');
  const [modelVersion, setModelVersion] = useState(latestVersion(FALLBACK_VERSIONS.dg) ?? 'v4');
  const [unCount, setUnCount] = useState(DEFAULT_UN_COUNT);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUN, setIsLoadingUN] = useState(false);
  const [isLoadingHS, setIsLoadingHS] = useState(false);
  const [isLoadingPreprocess, setIsLoadingPreprocess] = useState(false);
  const [isLoadingBooking, setIsLoadingBooking] = useState(false);
  const [result, setResult] = useState<DangerousGoodsResponse | null>(null);
  const [resultVersion, setResultVersion] = useState<string | null>(null);
  const [unResult, setUnResult] = useState<UNNumbersResponse | null>(null);
  const [hsResult, setHsResult] = useState<HSCodesResponse | null>(null);
  const [preprocessResult, setPreprocessResult] = useState<PreprocessingResponse | null>(null);
  const [bookingResult, setBookingResult] = useState<BookingClassificationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The result currently being flagged as wrong, and the last report that was kept
  const [flagged, setFlagged] = useState<FlaggedResult | null>(null);
  const [lastReport, setLastReport] = useState<FeedbackResponse | null>(null);

  // Which DGML deployments the backend can reach; one entry means no switch to show.
  useEffect(() => {
    let cancelled = false;

    apiService.getTargets()
      .then((response) => {
        if (cancelled) return;
        setTargets(response.targets);
        if (!response.targets.some((t) => t.name === apiService.getTarget())) {
          changeTarget(response.default);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('Target list unavailable, staying on the default:', err?.response?.status || err?.message);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount
  }, []);

  // Ask DGML what it serves so the dropdowns track deployments rather than a
  // hard-coded list. Older builds have no models endpoint; keep the fallback then.
  // Re-runs when the target changes, since dev and prod serve different sets.
  useEffect(() => {
    let cancelled = false;

    setVersionsFromServer(null);
    apiService.getModels()
      .then((models) => {
        if (cancelled) return;
        const grouped = groupEnabledVersions(models);
        setVersions(grouped);
        setVersionsFromServer(true);
        const latestDg = latestVersion(grouped.dg);
        if (latestDg) setModelVersion(latestDg);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('Model list unavailable, using fallback versions:', err?.response?.status || err?.message);
        setVersionsFromServer(false);
      });

    return () => {
      cancelled = true;
    };
  }, [target]);

  const busy = isLoading || isLoadingUN || isLoadingHS || isLoadingPreprocess || isLoadingBooking;

  // Results from one deployment say nothing about the other, so they go with the switch.
  const changeTarget = (name: string) => {
    apiService.setTarget(name);
    setTarget(name);
    setVersions(FALLBACK_VERSIONS);
    setResult(null);
    setResultVersion(null);
    setUnResult(null);
    setHsResult(null);
    setPreprocessResult(null);
    setBookingResult(null);
    setError(null);
    setLastReport(null);
  };

  const requireCargoText = (): string | null => {
    const text = cargoText.trim();
    if (!text) {
      setError('Please enter cargo description');
      return null;
    }
    return text;
  };

  const describeError = (err: any, fallback: string): string =>
    err.response?.data?.details || err.response?.data?.detail || err.response?.data?.error || fallback;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const text = requireCargoText();
    if (!text) return;

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const classification = await apiService.classifyDangerousGoods(text, modelVersion);
      setResult(classification);
      setResultVersion(modelVersion);
    } catch (err: any) {
      console.error('Classification error:', err);
      setError(describeError(err, 'Failed to classify cargo. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUNClassification = async () => {
    const text = requireCargoText();
    if (!text) return;

    setIsLoadingUN(true);
    setError(null);
    setUnResult(null);

    try {
      const unClassification = await apiService.classifyUNNumbers(text, latestVersion(versions.un) ?? 'v1', unCount);
      setUnResult(unClassification);
    } catch (err: any) {
      console.error('UN Classification error:', err);
      setError(describeError(err, 'Failed to classify UN numbers. Please try again.'));
    } finally {
      setIsLoadingUN(false);
    }
  };

  const handleHSClassification = async () => {
    const text = requireCargoText();
    if (!text) return;

    setIsLoadingHS(true);
    setError(null);
    setHsResult(null);

    try {
      const hsClassification = await apiService.classifyHSCodes(
        text, latestVersion(versions.hs) ?? 'v1', splitHsCodes(declaredHs));
      setHsResult(hsClassification);
    } catch (err: any) {
      console.error('HS Classification error:', err);
      setError(describeError(err, 'Failed to predict HS codes. Please try again.'));
    } finally {
      setIsLoadingHS(false);
    }
  };

  // Preprocessing versions track the DG versions: v4 is the cleaning dg v4 uses,
  // v5 adds the cargo cleaner and masker in front of the dg v5 encoder.
  const handlePreprocess = async () => {
    const text = requireCargoText();
    if (!text) return;

    setIsLoadingPreprocess(true);
    setError(null);
    setPreprocessResult(null);

    try {
      setPreprocessResult(await apiService.preprocessCargo(text, modelVersion));
    } catch (err: any) {
      console.error('Preprocessing error:', err);
      setError(describeError(err, 'Failed to preprocess cargo. Please try again.'));
    } finally {
      setIsLoadingPreprocess(false);
    }
  };

  // The pasted booking as an object, or null with the error shown. Parsed here so a
  // typo is reported before anything is sent.
  const requireBooking = (): object | null => {
    const text = bookingText.trim();
    if (!text) {
      setError('Please paste a booking as JSON');
      return null;
    }

    try {
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setError('The booking must be a JSON object');
        return null;
      }
      return parsed;
    } catch (err: any) {
      setError(`Not valid JSON: ${err.message}`);
      return null;
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const booking = requireBooking();
    if (!booking) return;

    setIsLoadingBooking(true);
    setError(null);
    setBookingResult(null);

    try {
      setBookingResult(await apiService.classifyBooking(booking, modelVersion, customer.trim(), unCount));
    } catch (err: any) {
      console.error('Booking classification error:', err);
      setError(describeError(err, 'Failed to classify the booking. Please try again.'));
    } finally {
      setIsLoadingBooking(false);
    }
  };

  const handleClear = () => {
    setCargoText('');
    setDeclaredHs('');
    setBookingText('');
    setResult(null);
    setResultVersion(null);
    setUnResult(null);
    setHsResult(null);
    setPreprocessResult(null);
    setBookingResult(null);
    setError(null);
  };

  // Results of one input mode mean nothing beside the other's form
  const changeInputMode = (mode: InputMode) => {
    setInputMode(mode);
    setError(null);
  };

  const flagResult = (flag: FlaggedResult) => {
    setLastReport(null);
    setFlagged(flag);
  };

  const handleFeedbackSubmitted = (response: FeedbackResponse) => {
    setFlagged(null);
    setLastReport(response);
  };

  const hsAvailable = versions.hs.length > 0;
  const embeddingsAvailable = versions.emb.length > 0;

  const nav: NavItem[] = [
    { key: 'classify', label: 'Classify', icon: <ShieldAlertIcon /> },
    {
      key: 'embeddings',
      label: 'Embeddings',
      icon: <ScatterIcon />,
      disabled: !embeddingsAvailable,
      title: embeddingsAvailable ? undefined : 'This DGML instance serves no embedding model',
    },
  ];

  const targetSwitch = targets.length > 1 && (
    <label className="target-switch">
      DGML
      <select
        className="input select input-sm"
        value={target}
        onChange={(e) => changeTarget(e.target.value)}
        disabled={busy}
        title={targets.find((t) => t.name === target)?.url}
      >
        {targets.map((t) => (
          <option key={t.name} value={t.name}>{t.name}</option>
        ))}
      </select>
    </label>
  );

  return (
    <AppShell
      title="Dangerous Goods Classifier"
      nav={nav}
      activeNav={view}
      onNavigate={(key) => setView(key as View)}
      headerActions={targetSwitch}
      userName={profile?.firstName || profile?.username || 'User'}
      onLogout={logout}
    >
      <div className="page">
        {view === 'embeddings' ? (
          <>
            <div className="page-intro">
              <h2 className="h3">Cargo Embeddings</h2>
              <p className="text-subtitle2 text-sm">Embed one or more descriptions and compare them</p>
            </div>
            <EmbeddingsExplorer key={target} versions={versions.emb} />
          </>
        ) : (
          <>
            <div className="page-intro">
              <h2 className="h3">Cargo Classification</h2>
              <p className="text-subtitle2 text-sm">
                Enter a description of your cargo to determine if it contains dangerous goods
              </p>
            </div>

            {versionsFromServer === false && (
              <div className="alert alert-warning" role="status">
                DGML did not report its model list; showing default versions.
              </div>
            )}

            <div className="content-layout">
              <section className="card form-card">
                <div className="segmented" role="radiogroup" aria-label="Input">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={inputMode === 'text'}
                    className={`segmented-item ${inputMode === 'text' ? 'is-active' : ''}`}
                    onClick={() => changeInputMode('text')}
                    disabled={busy}
                  >
                    Description
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={inputMode === 'booking'}
                    className={`segmented-item ${inputMode === 'booking' ? 'is-active' : ''}`}
                    onClick={() => changeInputMode('booking')}
                    disabled={busy}
                    title="Paste a whole booking as JSON and score it the way the live prediction job does"
                  >
                    Booking JSON
                  </button>
                </div>

                <form onSubmit={inputMode === 'booking' ? handleBookingSubmit : handleSubmit}>
                  {inputMode === 'booking' ? (
                    <>
                      <div className="field">
                        <label htmlFor="bookingText" className="field-label">Booking JSON</label>
                        <textarea
                          id="bookingText"
                          className="input textarea mono"
                          value={bookingText}
                          onChange={(e) => setBookingText(e.target.value)}
                          placeholder='Paste a shipment.schema.json booking, e.g. {"reference": "...", "cargo-transport-units": [...]}'
                          rows={14}
                          disabled={busy}
                          spellCheck={false}
                        />
                      </div>

                      <div className="field">
                        <label htmlFor="customer" className="field-label">Customer (optional)</label>
                        <input
                          id="customer"
                          type="text"
                          className="input"
                          value={customer}
                          onChange={(e) => setCustomer(e.target.value)}
                          placeholder="Customer slug, e.g. one-787f1, for the v5 customer prior"
                          disabled={busy}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="field">
                        <label htmlFor="cargoText" className="field-label">Cargo description</label>
                        <textarea
                          id="cargoText"
                          className="input textarea"
                          value={cargoText}
                          onChange={(e) => setCargoText(e.target.value)}
                          placeholder="Enter detailed description of the cargo (e.g., 'Lithium batteries', 'Paint thinner', 'Cotton fabric')"
                          rows={4}
                          disabled={busy}
                        />
                      </div>

                      {hsAvailable && (
                        <div className="field">
                          <label htmlFor="declaredHs" className="field-label">Declared HS codes (optional)</label>
                          <input
                            id="declaredHs"
                            type="text"
                            className="input"
                            value={declaredHs}
                            onChange={(e) => setDeclaredHs(e.target.value)}
                            placeholder="e.g. 3208.90, 38140000: scored beside the HS suggestions"
                            disabled={busy}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Collapsed by default: these are set once and rarely touched, and the
                      summary line still shows what is in effect */}
                  <details className="settings">
                    <summary className="settings-summary">
                      <span className="settings-title">Settings</span>
                      <span className="settings-current">
                        dg {modelVersion} · {unCount} UN {unCount === 1 ? 'number' : 'numbers'}
                      </span>
                    </summary>

                    <div className="settings-body">
                      <div className="settings-row">
                        <label htmlFor="modelVersion" className="settings-label">DG model</label>
                        <select
                          id="modelVersion"
                          className="input select settings-select"
                          value={modelVersion}
                          onChange={(e) => setModelVersion(e.target.value)}
                          disabled={busy}
                        >
                          {versions.dg.map((version) => (
                            <option key={version} value={version}>dg {version}</option>
                          ))}
                        </select>
                      </div>

                      <div className="settings-row">
                        <span id="unCountLabel" className="settings-label">UN numbers</span>
                        <div className="segmented small" role="radiogroup" aria-labelledby="unCountLabel">
                          {UN_COUNT_OPTIONS.map((count) => (
                            <button
                              key={count}
                              type="button"
                              role="radio"
                              aria-checked={unCount === count}
                              className={`segmented-item ${unCount === count ? 'is-active' : ''}`}
                              onClick={() => setUnCount(count)}
                              disabled={busy}
                            >
                              {count}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </details>

                  {inputMode === 'booking' ? (
                    <div className="form-actions">
                      <button
                        type="submit"
                        disabled={busy || !bookingText.trim()}
                        className="btn btn-primary btn-md"
                      >
                        {isLoadingBooking ? 'Classifying…' : 'Classify booking'}
                      </button>
                      <button
                        type="button"
                        onClick={handleClear}
                        disabled={busy}
                        className="btn btn-flat btn-md"
                      >
                        Clear
                      </button>
                    </div>
                  ) : (
                    <div className="form-actions">
                      <button
                        type="submit"
                        disabled={busy || !cargoText.trim()}
                        className="btn btn-primary btn-md"
                      >
                        {isLoading ? 'Classifying…' : 'Classify dangerous goods'}
                      </button>
                      <button
                        type="button"
                        onClick={handleUNClassification}
                        disabled={busy || !cargoText.trim()}
                        className="btn btn-outline btn-md"
                      >
                        {isLoadingUN ? 'Finding UN numbers…' : 'Find UN numbers'}
                      </button>
                      <button
                        type="button"
                        onClick={handleHSClassification}
                        disabled={busy || !cargoText.trim() || !hsAvailable}
                        className="btn btn-outline btn-md"
                        title={hsAvailable ? undefined : 'This DGML instance serves no HS model'}
                      >
                        {isLoadingHS ? 'Predicting HS codes…' : 'Predict HS codes'}
                      </button>
                      <button
                        type="button"
                        onClick={handlePreprocess}
                        disabled={busy || !cargoText.trim()}
                        className="btn btn-outline btn-md"
                        title="Show the text as the selected DG model would see it, without scoring"
                      >
                        {isLoadingPreprocess ? 'Preprocessing…' : 'Show model input'}
                      </button>
                      <button
                        type="button"
                        onClick={handleClear}
                        disabled={busy}
                        className="btn btn-flat btn-md"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </form>
              </section>

              <section className="results-section">
                {error && (
                  <div className="alert alert-error" role="alert">
                    <p className="alert-title">Error</p>
                    <p>{error}</p>
                  </div>
                )}

                {lastReport && (
                  <div className="alert alert-success" role="status">
                    <span>Report kept as <code>{lastReport.cargoUid}</code>.</span>
                    {lastReport.storedIn === 'local' && (
                      <span> Held on the server for now; blob storage would not take it.</span>
                    )}
                  </div>
                )}

                {bookingResult && inputMode === 'booking' && <BookingResult result={bookingResult} />}

                {inputMode === 'text' && result && (
                  <div className="card result-card">
                    <div className="result-header">
                      <span className="h5">Dangerous goods</span>
                      <span className="result-tags">
                        {resultVersion && <span className="tag">dg {resultVersion}</span>}
                        <span className={`tag tag-verdict ${result.isDangerous ? 'dangerous' : 'safe'}`}>
                          {result.isDangerous ? 'Dangerous' : 'Safe'}
                        </span>
                        {result.confidence !== undefined && (
                          <span className="tag tag-score">
                            {(result.confidence * 100).toFixed(0)}%
                          </span>
                        )}
                      </span>
                    </div>

                    {result.confidence !== undefined && (
                      <div className="meter">
                        <div
                          className={`meter-fill ${result.isDangerous ? 'dangerous' : 'safe'}`}
                          style={{ width: `${result.confidence * 100}%` }}
                        />
                      </div>
                    )}

                    {result.reasons && result.reasons.length > 0 && (
                      <div className="result-note" style={{ marginTop: '0.5rem' }}>
                        {result.reasons[0]}
                      </div>
                    )}

                    {(result.unRuleDg || result.scored === false) && (
                      <div className="verdict-notes">
                        {result.unRuleDg && (
                          <div>Declared UN number with no exempting special provision: verdict forced to dangerous.</div>
                        )}
                        {result.scored === false && (
                          <div>Nothing in the text could be scored; the probability is the customer prior.</div>
                        )}
                      </div>
                    )}

                    <div className="result-actions">
                      <button
                        onClick={() => flagResult({
                          family: 'dg',
                          modelVersion: result.modelVersion ?? resultVersion ?? modelVersion,
                          cargo: result.cargo,
                          response: result.raw,
                          said: result.isDangerous ? 'Dangerous goods' : 'Not dangerous goods'
                        })}
                        className="btn btn-outline btn-xs"
                      >
                        Flag as wrong
                      </button>
                    </div>
                  </div>
                )}

                {inputMode === 'text' && unResult && (
                  <div className="card result-card">
                    <div className="result-header">
                      <span className="h5">UN numbers</span>
                      {unResult.modelVersion && <span className="tag">un {unResult.modelVersion}</span>}
                    </div>

                    {unResult.topPrediction && (
                      <div>
                        <div className="prediction-primary">
                          <span className="prediction-code">{unResult.topPrediction.prediction.toUpperCase()}</span>
                          <span className="tag tag-score">
                            {(unResult.topPrediction.probability * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div className="meter">
                          <div
                            className="meter-fill un"
                            style={{ width: `${unResult.topPrediction.probability * 100}%` }}
                          />
                        </div>
                        <ValidateEntries lookup={unResult.topPrediction.validate} />
                      </div>
                    )}

                    {unResult.unNumbers && unResult.unNumbers.length > 1 && (
                      <div className="prediction-alternatives">
                        {unResult.unNumbers.slice(1).map((pred, index) => (
                          <div key={index}>
                            <div className="prediction-alt-label">
                              <strong>{pred.prediction.toUpperCase()}</strong>
                              <span className="score">{(pred.probability * 100).toFixed(0)}%</span>
                            </div>
                            <div className="meter small">
                              <div
                                className="meter-fill un alt"
                                style={{ width: `${pred.probability * 100}%` }}
                              />
                            </div>
                            <ValidateEntries lookup={pred.validate} compact />
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="result-actions">
                      <button
                        onClick={() => flagResult({
                          family: 'un',
                          modelVersion: unResult.modelVersion ?? 'v1',
                          cargo: unResult.cargo,
                          response: unResult.raw,
                          said: unResult.topPrediction
                            ? unResult.topPrediction.prediction.toUpperCase()
                            : 'no UN number'
                        })}
                        className="btn btn-outline btn-xs"
                      >
                        Flag as wrong
                      </button>
                    </div>
                  </div>
                )}

                {inputMode === 'text' && preprocessResult && <PreprocessingResult result={preprocessResult} />}

                {inputMode === 'text' && hsResult && (
                  <HsCodeResult
                    result={hsResult}
                    onFlag={() => flagResult({
                      family: 'hs',
                      modelVersion: hsResult.modelVersion,
                      cargo: hsResult.cargo,
                      response: hsResult.raw,
                      said: hsResult.predictions[0]?.hs6 ?? 'no HS code'
                    })}
                  />
                )}
              </section>
            </div>
          </>
        )}
      </div>

      {flagged && (
        <FeedbackModal
          flagged={flagged}
          onClose={() => setFlagged(null)}
          onSubmitted={handleFeedbackSubmitted}
        />
      )}
    </AppShell>
  );
};

export default DangerousGoodsClassifier;
